/*
 * API Endpoint Configuration
 *
 * To add a new V2 endpoint:
 * 1. Add endpoint with urlPatterns (exact URL strings from ApiConstants)
 * 2. Set v2Available: true when backend V2 is deployed
 * 3. Set defaultVersion: 'v1' or 'v2' to specify the code-level default version
 *    - Priority: localStorage override → defaultVersion → 'v1' fallback
 * 4. For dynamic URLs like /growers/:id, use { base: apiConstants.GROWERS, dynamic: true }
 */

import * as apiConstants from '../components/constants/ApiConstants';

export const ENDPOINT_GROUPS = [
  {
    title: 'Settings',
    id: 'group_settings',
    endpoints: [
      {
        id: 'get_settings',
        label: 'Get Settings',
        urlPatterns: [apiConstants.GET_SETTINGS],
        version: 'v1',
      },
      {
        id: 'save_apply_due_date',
        label: 'Save Apply Due Date',
        urlPatterns: [apiConstants.SAVE_APPLY_DUE_DATE],
        version: 'v1',
      },
      {
        id: 'update_sa_status',
        label: 'Update SA Status',
        urlPatterns: [apiConstants.UPDATE_SEED_ADVISOR_STATUS],
        version: 'v1',
      },
    ],
  },
  {
    title: 'User & Authentication',
    id: 'group_user_auth',
    endpoints: [
      {
        id: 'get_user_profile',
        label: 'Get User Profile',
        urlPatterns: [
          { base: apiConstants.LOGIN_GET_USER_PROFILE_BASE, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'verify_user',
        label: 'Verify User',
        urlPatterns: [apiConstants.VERIFY_USER],
        version: 'v1',
      },
      {
        id: 'password_management',
        label: 'Password Management',
        urlPatterns: [
          apiConstants.RESET_PASSWORD,
          apiConstants.EMAIL_FORGOT_PASSWORD,
          apiConstants.VALIDATE_PASSWORD_TOKEN,
          apiConstants.ADMIN_CHANGE_GROWER_PASSWORD,
          apiConstants.ADMIN_RESET_GROWER_PASSWORD,
        ],
        version: 'v1',
      },
      {
        id: 'mfa',
        label: 'MFA (Multi-Factor Auth)',
        urlPatterns: [
          apiConstants.SMS_FORGOT_PASSWORD_IS_PHONE_NUMBER_EXISTS,
          apiConstants.MFA_VERIFY_PHONE,
          apiConstants.MFA_VERIFY_OTP,
        ],
        version: 'v1',
      },
      {
        id: 'okta_error',
        label: 'Okta Error Logging',
        urlPatterns: [apiConstants.SET_OKTA_ERROR],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Growers',
    id: 'group_growers',
    endpoints: [
      {
        id: 'growers_base',
        label: 'Grower Details & Actions',
        urlPatterns: [{ base: apiConstants.GROWERS, dynamic: true }],
        version: 'v1',
      },
      {
        id: 'add_new_grower',
        label: 'Add New Grower (Signup)',
        urlPatterns: [apiConstants.ADD_NEW_GROWER],
        version: 'v1',
      },
      {
        id: 'apply_for_credit',
        label: 'Apply for Extended Terms',
        urlPatterns: [
          apiConstants.APPLY_FOR_CREDIT,
          apiConstants.APPLY_FOR_NEW_CREDIT,
        ],
        version: 'v1',
      },
      {
        id: 'grower_application',
        label: 'Grower Application',
        urlPatterns: [apiConstants.GROWER_APPLICATION],
        version: 'v1',
      },
      {
        id: 'grower_seed_advisor',
        label: 'Grower Seed Advisor',
        urlPatterns: [
          { base: apiConstants.GROWER_SEED_ADVISOR, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'update_grower_farm_address',
        label: 'Update Grower Farm Address',
        urlPatterns: [apiConstants.UPDATE_GROWER_FARM_ADDRESS],
        version: 'v1',
      },
      {
        id: 'email_seed_advisor',
        label: 'Email Seed Advisor',
        urlPatterns: [apiConstants.EMAIL_SEED_ADVISOR],
        version: 'v1',
      },
      {
        id: 'get_bulletins',
        label: 'Get Bulletins',
        urlPatterns: [{ base: apiConstants.GET_BULLETINS, dynamic: true }],
        version: 'v1',
      },
      {
        id: 'get_grower_by_primary_sa',
        label: 'Get Grower by Primary SA ID',
        urlPatterns: [
          { base: apiConstants.GET_GROWER_BY_PRIMARY_SA_ID, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'indv_grower_profile',
        label: 'Individual Grower Profile (Parent/Grower)',
        urlPatterns: [
          { base: apiConstants.INDV_GROWER_PROFILE, dynamic: true },
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Seed Advisors',
    id: 'group_seed_advisors',
    endpoints: [
      {
        id: 'seed_advisor_base',
        label: 'Seed Advisor Operations',
        urlPatterns: [{ base: apiConstants.SEED_ADVISOR, dynamic: true }],
        version: 'v1',
      },
      {
        id: 'sa_setup_account',
        label: 'SA Setup Account',
        urlPatterns: [apiConstants.SA_SETUP_ACCOUNT],
        version: 'v1',
      },
      {
        id: 'sa_login_account',
        label: 'SA Login',
        urlPatterns: [apiConstants.SA_Login_ACCOUNT],
        version: 'v1',
      },
      {
        id: 'zd_partnerships',
        label: 'ZD Partnerships',
        urlPatterns: [
          { base: apiConstants.ZD_PARTNERSHIPS, dynamic: true },
          { base: apiConstants.GET_ZD_PARTNERSHIP_BY_SA_ID, dynamic: true },
          { base: apiConstants.UPDATE_ZD_GROWER_EMAIL, dynamic: true },
          { base: apiConstants.RESET_INVITE, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'get_sa_for_grower',
        label: 'Get SA for Grower',
        urlPatterns: [apiConstants.GET_SA_FOR_GROWER],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Bulletins & Resources',
    id: 'group_resources',
    endpoints: [
      {
        id: 'bulletins',
        label: 'Bulletins',
        urlPatterns: [
          { base: apiConstants.BULLETIN, dynamic: true },
          { base: apiConstants.BULLETINS, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'resources',
        label: 'Resources (Download/Upload)',
        urlPatterns: [
          { base: apiConstants.BASE_RESOURCE_URL, dynamic: true },
          apiConstants.SAVE_RESOURCE,
          apiConstants.UPDATE_RESOURCES,
        ],
        version: 'v1',
      },
      {
        id: 'get_most_recent_tc',
        label: 'Get Most Recent T&C Resources',
        urlPatterns: [apiConstants.GET_MOST_RECENT_TC_RESOURCES],
        version: 'v1',
      },
      {
        id: 'export_data',
        label: 'Export Data',
        urlPatterns: [apiConstants.EXPORT_DATA],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Admin',
    id: 'group_admin',
    endpoints: [
      {
        id: 'admin_grower_payments',
        label: 'Grower Payments & Emails',
        urlPatterns: [
          apiConstants.GET_ALL_GROWERS_ADMIN,
          apiConstants.EMAIL_GROWERS_ADMIN,
        ],
        version: 'v1',
      },
      {
        id: 'admin_batch_jobs',
        label: 'Batch Job Management',
        urlPatterns: [
          apiConstants.GET_BATCH_JOB_HISTORY,
          apiConstants.GET_BATCH_JOB_CONFIGURATIONS,
          apiConstants.SAVE_BATCH_JOB,
          { base: apiConstants.DELETE_BATCH_JOB, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'admin_operations',
        label: 'Admin Operations',
        urlPatterns: [
          apiConstants.DELETE_GROWER_APPLICATIONS,
          apiConstants.UNSET_SA_ACKNOWLEDGEMENT_SENT,
          apiConstants.RESET_INVITE_FLAG_ZD_PARTNERSHIP,
          apiConstants.COPY_PRIOR_SEASON_BUDGET,
          apiConstants.GET_LATEST_LOG_FOR_ALL_SEASON,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Statements & Balances',
    id: 'group_statements',
    endpoints: [
      {
        id: 'statement_balances',
        label: 'Statement Balances',
        urlPatterns: [
          apiConstants.GET_STATEMENT_BALANCES,
          apiConstants.GET_STATEMENT_PDF,
          apiConstants.GROWER_STATEMENTS_EXPORT_ALL,
        ],
        version: 'v1',
      },
      {
        id: 'admin_statements',
        label: 'Admin Statement Operations',
        urlPatterns: [
          apiConstants.SEARCH_ADMIN_STATEMENTS,
          apiConstants.GROWER_STATEMENTS_ADMIN_EXPORT,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Statement Marketing',
    id: 'group_statement_marketing',
    endpoints: [
      {
        id: 'statement_marketing_ads',
        label: 'Statement Marketing Ads',
        urlPatterns: [
          apiConstants.GET_STATEMENT_MARKETING_ADS,
          { base: apiConstants.DELETE_STATEMENT_MARKETING_AD, dynamic: true },
          { base: apiConstants.VIEW_STATEMENT_MARKETING_AD, dynamic: true },
          apiConstants.SAVE_STATEMENT_MARKETING_AD,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Sales Representatives',
    id: 'group_sales_reps',
    endpoints: [
      {
        id: 'sr_growers',
        label: 'SR Grower Management',
        urlPatterns: [
          { base: apiConstants.GET_GROWERS_SA_BY_SALES_REP_ID, dynamic: true },
          { base: apiConstants.GET_GROWERS_SA_BY_SALES_REP, dynamic: true },
          { base: apiConstants.INDV_SA_GROWER_PROFILE_BY_SR, dynamic: true },
          { base: apiConstants.LOAD_GROWER_STATEMENT_BY_SR, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'sr_seed_advisors',
        label: 'SR Seed Advisor Operations',
        urlPatterns: [
          { base: apiConstants.GET_SEED_ADVISER_BY_SA, dynamic: true },
          {
            base: apiConstants.GET_ZD_PARTNERSHIP_BY_SA_ID_BY_SR,
            dynamic: true,
          },
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Customer Support (CAS)',
    id: 'group_cas',
    endpoints: [
      {
        id: 'cas_dashboard',
        label: 'CAS Dashboard & Data',
        urlPatterns: [
          { base: apiConstants.CAS_GET_DASHBOARD, dynamic: true },
          { base: apiConstants.CAS_GET_COLLECTION_DATA, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'cas_growers_sa',
        label: 'CAS Growers & Seed Advisors',
        urlPatterns: [
          { base: apiConstants.CAS_ALL_GROWER, dynamic: true },
          { base: apiConstants.CAS_ALL_SA, dynamic: true },
        ],
        version: 'v1',
      },
      {
        id: 'cas_utilities',
        label: 'CAS Utilities',
        urlPatterns: [
          apiConstants.CAS_GET_DISTRICTS,
          apiConstants.CAS_GET_GROWER_STATES,
          apiConstants.CAS_SEARCH,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Seed Care Reports',
    id: 'group_seed_care',
    endpoints: [
      {
        id: 'seed_care_reports',
        label: 'Seed Care Reports',
        urlPatterns: [
          { base: apiConstants.SA_GET_SEED_CARE_REPORT, dynamic: true },
          { base: apiConstants.CAS_GET_SEED_CARE_REPORT, dynamic: true },
          apiConstants.CAS_GET_SEED_CARE_REPORT_DISTRICTS,
          { base: apiConstants.SR_GET_SEED_CARE_REPORT, dynamic: true },
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Finance Upload',
    id: 'group_finance',
    endpoints: [
      {
        id: 'finance_upload',
        label: 'Finance Upload Operations',
        urlPatterns: [
          apiConstants.FINANCE_UPLOAD_FILE,
          apiConstants.GET_FINANCE_UPLOAD_LOGS,
          apiConstants.DOWNLOAD_FINANCE_SAMPLE_CSV,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Season & Configuration',
    id: 'group_season_config',
    endpoints: [
      {
        id: 'season_config',
        label: 'Season Configuration',
        urlPatterns: [
          apiConstants.SET_CROP_START_YEAR,
          apiConstants.GET_SEASON_LAUNCH_CONFIG,
          apiConstants.GET_CURRENT_CROP_SALES_YEAR,
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Payments & Transactions',
    id: 'group_payments',
    endpoints: [
      {
        id: 'grower_payments',
        label: 'Grower Payments',
        urlPatterns: [
          apiConstants.MAKE_PAYMENT,
          apiConstants.UPDATE_JPM_TRANSACTION,
          {
            base: apiConstants.GET_GROWER_SAP_PAYMENT_COMPARISON,
            dynamic: true,
          },
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Notifications',
    id: 'group_notifications',
    endpoints: [
      {
        id: 'grower_notifications',
        label: 'Grower Notifications',
        urlPatterns: [
          { base: apiConstants.GROWER_NOTIFICATIONS, dynamic: true },
          { base: apiConstants.UPDATE_GROWER_NOTIFICATIONS, dynamic: true },
        ],
        version: 'v1',
      },
    ],
  },
  {
    title: 'Emails',
    id: 'group_emails',
    endpoints: [
      {
        id: 'email_operations',
        label: 'Email Operations',
        urlPatterns: [
          apiConstants.EMAIL_GROWER,
          apiConstants.INVITE_EXISTING_GROWER,
          apiConstants.EMAIL_SEED_ADVISOR,
        ],
        version: 'v1',
      },
    ],
  },
];
